/**
 * EventEditDrawer — slide-out (sheet) ops editor for any event card on
 * `/calendar`. Mounts the FULL field set (name, time window, address,
 * visibility, event type, prep, staff_needed, ops_notes, staff
 * assignment dropdown) so the owner can edit anything from the
 * calendar without jumping back to the Dashboard.
 *
 * Works for every event source (manual `daily` posts, weekly
 * `schedule` entries, and `google_calendar` rows). For GCal-origin
 * events the backend stamps `manual_overrides` on each changed field
 * so the 5-min pull cron stops clobbering the edit, and pushes the
 * change back to Google for true two-way sync.
 *
 * Staff assignment: multi-select dropdown sourced from `/owner/staff`
 * (active invites only). Newly-added assignees get an email on save
 * (backend dispatches via `_dispatch_event_assignment_email`).
 *
 * Phase 1C — Feb 28 2026.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { etTimeMs, etWallString } from "../lib/etTime";
import { parseLatLng, reverseGeocode } from "../lib/geocode";

const VISIBILITIES = [
  { value: "public",  label: "Public — appears on the customer map" },
  { value: "staff",   label: "Staff — hidden from customers, visible to crew" },
  { value: "private", label: "Private — only you can see this" },
];

const EVENT_TYPES = [
  { value: "street",         label: "Street / curbside" },
  { value: "festival",       label: "Festival" },
  { value: "catering",       label: "Catering" },
  { value: "private_party",  label: "Private party" },
  { value: "popup",          label: "Pop-up" },
  { value: "farmers_market", label: "Farmers market" },
  { value: "brewery",        label: "Brewery / taproom" },
  { value: "corporate",      label: "Corporate" },
  { value: "other",          label: "Other" },
];

// ISO datetime ↔ <input type="datetime-local"> bridge. The browser
// expects "YYYY-MM-DDTHH:MM" (no timezone, no seconds). We render and
// interpret the input as ET wall-clock time (the app's canonical
// timezone) regardless of the editor's device timezone.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const ms = etTimeMs(iso);
  if (!Number.isFinite(ms)) return "";
  return etWallString(ms).slice(0, 16);
}
function localInputToIso(local) {
  if (!local) return null;
  // Input is ET wall time — store as a naive ET wall-clock string,
  // the app's storage convention (parsed as ET everywhere).
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(local)) return null;
  return local.length === 16 ? `${local}:00` : local;
}

export default function EventEditDrawer({ open, onOpenChange, event, onSaved }) {
  // Local form state — re-seeded whenever the parent passes in a new
  // event. We keep this fully controlled so the user can edit any
  // field, then either save or discard.
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);

  const isGcal = event?.source === "google_calendar";

  useEffect(() => {
    if (!event) {
      setForm(null);
      return;
    }
    setForm({
      event_name: event.event_name || "",
      address: event.address || "",
      starts_at: isoToLocalInput(event.starts_at),
      ends_at: isoToLocalInput(event.ends_at),
      menu_text: event.menu_text || "",
      visibility: event.visibility || "public",
      event_type: event.event_type || "",
      prep_count: event.prep_count ?? "",
      staff_needed: event.staff_needed ?? "",
      prep_notes: event.prep_notes || "",
      ops_notes: event.ops_notes || "",
      assigned_staff_invite_ids: Array.isArray(event.assigned_staff_invite_ids)
        ? [...event.assigned_staff_invite_ids]
        : [],
      // Phase 1E — owner is implicitly on shift unless they opt out.
      // Default to true for legacy rows where the field is missing.
      owner_on_shift: event.owner_on_shift !== false,
    });
  }, [event]);

  // Load active staff invites the first time the drawer opens.
  useEffect(() => {
    if (!open) return;
    setStaffLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/owner/staff");
        setStaffOptions(
          (data || []).filter((i) => i.status !== "revoked"),
        );
      } catch {
        setStaffOptions([]);
      } finally {
        setStaffLoading(false);
      }
    })();
  }, [open]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleStaff = (inviteId) => {
    setForm((f) => {
      const cur = new Set(f.assigned_staff_invite_ids || []);
      if (cur.has(inviteId)) cur.delete(inviteId);
      else cur.add(inviteId);
      return { ...f, assigned_staff_invite_ids: Array.from(cur) };
    });
  };

  const newlyAddedCount = useMemo(() => {
    if (!form || !event) return 0;
    const before = new Set(event.assigned_staff_invite_ids || []);
    return (form.assigned_staff_invite_ids || []).filter(
      (id) => !before.has(id),
    ).length;
  }, [form, event]);

  // Coordinates pasted into the address box (Google Maps copy-paste)?
  // On blur: pin the exact spot + reverse-geocode a display address.
  const onAddressBlur = async () => {
    const coords = parseLatLng(form?.address);
    if (!coords) return;
    const r = await reverseGeocode(coords.lat, coords.lng);
    if (r?.address) {
      update({ address: r.address, lat: coords.lat, lng: coords.lng });
      toast.success("Coordinates pinned — address filled in", { description: r.address });
    } else {
      update({ lat: coords.lat, lng: coords.lng });
      toast.message("Pinned your exact coordinates", {
        description:
          "No street address found — type a short description in the address box (the pin keeps your coordinates).",
        duration: 10000,
      });
    }
  };


  const save = async () => {
    if (!event || !form) return;
    setSaving(true);
    try {
      const payload = {
        event_name: form.event_name || null,
        address: form.address || null,
        starts_at: localInputToIso(form.starts_at),
        ends_at: localInputToIso(form.ends_at),
        menu_text: form.menu_text || "",
        visibility: form.visibility || "public",
        event_type: form.event_type || null,
        prep_count: form.prep_count === "" ? null : Number(form.prep_count),
        staff_needed: form.staff_needed === "" ? null : Number(form.staff_needed),
        prep_notes: form.prep_notes || null,
        ops_notes: form.ops_notes || null,
        assigned_staff_invite_ids: form.assigned_staff_invite_ids || [],
        owner_on_shift: !!form.owner_on_shift,
        // Only send a pin when the coords-paste flow set one — otherwise
        // leave the event's existing pin untouched.
        ...(form.lat != null && form.lng != null
          ? { lat: form.lat, lng: form.lng }
          : {}),
      };
      await api.put(`/trucks/${event.id}`, payload);
      const msg = newlyAddedCount > 0
        ? `Event updated · ${newlyAddedCount} crew queued for digest`
        : "Event updated";
      toast.success(msg, {
        description: newlyAddedCount > 0
          ? "Auto-sends in ≤10 min — or hit \"Send all now\" on the calendar banner to flush right away."
          : (isGcal
            ? "Synced back to Google Calendar — your edits will stick across syncs."
            : undefined),
      });
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Couldn't save the event");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="event-edit-drawer"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Edit event
            {isGcal && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-primary/10 text-primary px-2 py-0.5"
                data-testid="event-edit-drawer-gcal-badge"
              >
                GCal · 2-way
              </span>
            )}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {isGcal
              ? "Your edits stick on next Google Calendar sync and get pushed back to Google."
              : "Updates this event everywhere it shows — map, calendar, staff view."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* --- Basics --- */}
          <div className="space-y-1.5">
            <Label htmlFor="event_name" className="text-xs">Event name</Label>
            <Input
              id="event_name"
              value={form.event_name}
              onChange={(e) => update({ event_name: e.target.value })}
              placeholder="e.g. Ember @ Landisville Posse"
              data-testid="event-edit-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="address" className="text-xs">
              Address
              {(() => {
                const t = (form.event_name || "").toLowerCase();
                const looksPrivate =
                  form.visibility === "private" ||
                  form.visibility === "staff" ||
                  /\b(private|closed|vacation|off\s*day|day\s*off|no\s*service|family\s*emergency|internal|training|prep|holiday)\b/i.test(t);
                return looksPrivate ? (
                  <span className="ml-2 text-[10px] font-normal text-muted-foreground" data-testid="event-edit-address-optional">
                    · optional for private events
                  </span>
                ) : null;
              })()}
            </Label>
            <Input
              id="address"
              value={form.address}
              onChange={(e) => update({ address: e.target.value })}
              onBlur={onAddressBlur}
              placeholder="Street, City, State ZIP — or paste coordinates"
              data-testid="event-edit-address"
            />
            {form.lat != null && form.lng != null && (
              <p className="text-[11px] text-primary" data-testid="event-edit-pin-indicator">
                ✓ Pin set to exact coordinates ({Number(form.lat).toFixed(4)}, {Number(form.lng).toFixed(4)})
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="starts_at" className="text-xs">Starts</Label>
              <Input
                id="starts_at"
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => update({ starts_at: e.target.value })}
                data-testid="event-edit-starts"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ends_at" className="text-xs">Ends</Label>
              <Input
                id="ends_at"
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => update({ ends_at: e.target.value })}
                data-testid="event-edit-ends"
              />
            </div>
          </div>

          {/* --- Visibility + type --- */}
          <div className="space-y-1.5">
            <Label className="text-xs">Visibility</Label>
            <Select
              value={form.visibility}
              onValueChange={(v) => update({ visibility: v })}
            >
              <SelectTrigger data-testid="event-edit-visibility">
                <SelectValue placeholder="Visibility" />
              </SelectTrigger>
              <SelectContent>
                {VISIBILITIES.map((v) => (
                  <SelectItem key={v.value} value={v.value} data-testid={`event-edit-visibility-${v.value}`}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Event type</Label>
            <Select
              value={form.event_type || "__none"}
              onValueChange={(v) => update({ event_type: v === "__none" ? "" : v })}
            >
              <SelectTrigger data-testid="event-edit-event-type">
                <SelectValue placeholder="(none)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">(none)</SelectItem>
                {EVENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} data-testid={`event-edit-event-type-${t.value}`}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* --- Ops counts --- */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="prep_count" className="text-xs">Prep count</Label>
              <Input
                id="prep_count"
                type="number"
                min="0"
                value={form.prep_count}
                onChange={(e) => update({ prep_count: e.target.value })}
                placeholder="200"
                data-testid="event-edit-prep-count"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="staff_needed" className="text-xs">Staff needed</Label>
              <Input
                id="staff_needed"
                type="number"
                min="0"
                value={form.staff_needed}
                onChange={(e) => update({ staff_needed: e.target.value })}
                placeholder="3"
                data-testid="event-edit-staff-needed"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="prep_notes" className="text-xs">Prep checklist</Label>
            <Textarea
              id="prep_notes"
              value={form.prep_notes}
              onChange={(e) => update({ prep_notes: e.target.value })}
              rows={2}
              placeholder="Extra cheese sauce, double the brisket…"
              data-testid="event-edit-prep-notes"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ops_notes" className="text-xs">Ops notes (private)</Label>
            <Textarea
              id="ops_notes"
              value={form.ops_notes}
              onChange={(e) => update({ ops_notes: e.target.value })}
              rows={2}
              placeholder="Parking on west side, gate code 4321, contact Sam 555-1234"
              data-testid="event-edit-ops-notes"
            />
          </div>

          {/* --- Staff assignment --- */}
          <div className="space-y-2 pt-2 border-t border-border">
            <Label className="text-xs flex items-center justify-between">
              <span>Crew</span>
              {newlyAddedCount > 0 && (
                <span className="text-[10px] font-semibold text-primary" data-testid="event-edit-staff-new-count">
                  {newlyAddedCount} new — queued for digest
                </span>
              )}
            </Label>
            {/* Phase 1E — owner is on shift by default. Opt-out per
                event when the lead is running it solo. Rendered AS A
                CREW ROW to match the visual rhythm of the assigned
                staff list below. */}
            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-2 py-1.5">
              <Checkbox
                id="owner-on-shift"
                checked={!!form.owner_on_shift}
                onCheckedChange={(v) => update({ owner_on_shift: !!v })}
                data-testid="event-edit-owner-on-shift"
              />
              <label
                htmlFor="owner-on-shift"
                className="text-sm cursor-pointer flex-1 min-w-0"
              >
                <div className="font-medium truncate">Me (owner) on this shift</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  Auto-added to every event &middot; uncheck to sit this one out
                </div>
              </label>
            </div>
            {staffLoading ? (
              <p className="text-xs text-muted-foreground">Loading crew…</p>
            ) : staffOptions.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No active staff yet. Add a crew member in the Staff section above first.
              </p>
            ) : (
              <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="event-edit-staff-list">
                {staffOptions.map((s) => {
                  const checked = (form.assigned_staff_invite_ids || []).includes(s.id);
                  return (
                    <li key={s.id} className="flex items-start gap-2">
                      <Checkbox
                        id={`assign-${s.id}`}
                        checked={checked}
                        onCheckedChange={() => toggleStaff(s.id)}
                        data-testid={`event-edit-staff-toggle-${s.id}`}
                      />
                      <label
                        htmlFor={`assign-${s.id}`}
                        className="text-sm cursor-pointer flex-1 min-w-0"
                      >
                        <div className="font-medium truncate">{s.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {s.email || "no email on file — won't be notified"}
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="text-[11px] text-muted-foreground leading-snug">
              Newly-added crew get a digest email batching all their new shifts &mdash; auto-sends
              in ≤10 min so back-to-back assignments don&rsquo;t spam them. Use the orange
              banner on the calendar to flush early.
            </p>
          </div>

          {/* --- Save / cancel --- */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              data-testid="event-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={save}
              disabled={saving}
              className="flex-1"
              data-testid="event-edit-save"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
